import type { AlignedGoal } from '@/resources/ResourceAlignedGoal.vue'
import { GRADES } from '@/browse/grades'
import { isCoreSubject } from '@/browse/assessments/assessment_taxonomy'

const PROGRESS_MONITORING = 'Progress Monitoring'
const BEHAVIOR_DATA_COLLECTION = 'Behavior Data Collection'
// Not a grade, so neither shown nor ranked.
const MASTERY = 'Mastery'
// Matches present_levels_version on an indexed alignment, written by resource_alignments_mapper.
const CURRENT_PRESENT_LEVELS_VERSION = '2'

// GRADES lists the grades lowest first, so a grade's position is its rank.
function gradeRank(grade: string): number {
  return GRADES.indexOf(grade)
}

// An assessment with no grades ranks alongside Pre-K.
const UNGRADED_RANK = gradeRank('Pre-K')

export interface AssessmentHit {
  objectID: string
  uuid: string
  title: string
  description: string
  type: string
  thumbnail_url: string
  grades?: string[]
  levels?: string[]
  subjects?: string[]
  subject?: string[]
  domains?: string[]
  screenshots?: string[]
  download_links: unknown[]
  google_drive_links?: unknown[]
  content_alignments?: {
    goals?: AlignedGoal[]
    present_levels_alignments?: { present_levels_version: string }[]
  } | null
  set_number?: number
  rationale?: string
  relevance_score?: number
}

function realGrades(assessment: AssessmentHit): string[] {
  return (assessment.grades ?? []).filter((grade) => grade && grade !== MASTERY)
}

function highestGradeRank(assessment: AssessmentHit): number {
  const ranks = realGrades(assessment).map(gradeRank)
  if (!ranks.length) {
    return UNGRADED_RANK
  }
  return Math.max(...ranks)
}

export function sortByHighestGradeFirst<T extends AssessmentHit>(assessments: T[]): T[] {
  return [...assessments].sort((assessmentA, assessmentB) => highestGradeRank(assessmentB) - highestGradeRank(assessmentA))
}

export function subjectsOf(assessment: AssessmentHit): string[] {
  return assessment.subjects ?? assessment.subject ?? []
}

export function firstAlignedGoal(assessment: AssessmentHit): AlignedGoal | undefined {
  return assessment.content_alignments?.goals?.[0]
}

function isProgressMonitoring(assessment: AssessmentHit): boolean {
  return assessment.type === PROGRESS_MONITORING
}

// A present-levels-2 alignment resolves one real grade per aligned level, so its grade is worth
// showing for any subject. A legacy present-levels baseline on a non-academic subject is widened
// to every grade from Pre-K to 12, which is why those fall through to the level instead.
function isAlignedThroughCurrentPresentLevels(assessment: AssessmentHit): boolean {
  const alignments = assessment.content_alignments?.present_levels_alignments ?? []
  return alignments.some((alignment) => alignment.present_levels_version === CURRENT_PRESENT_LEVELS_VERSION)
}

function showsGrades(assessment: AssessmentHit, isFromWizard: boolean): boolean {
  if (isAlignedThroughCurrentPresentLevels(assessment)) {
    return true
  }
  // Only a missing subjects field hides grades; an empty one still shows them.
  const hasSubjectsField = Boolean(assessment.subjects ?? assessment.subject)
  if (!hasSubjectsField) {
    return false
  }
  const isInCoreSubject = isCoreSubject(subjectsOf(assessment)[0])
  return isFromWizard || isProgressMonitoring(assessment) || isInCoreSubject
}

function showsLevels(assessment: AssessmentHit): boolean {
  if (!assessment.subject) {
    return false
  }
  const isBehaviorDataCollection = (assessment.domains ?? []).includes(BEHAVIOR_DATA_COLLECTION)
  const isCoreProgressMonitoring = isProgressMonitoring(assessment) && isCoreSubject(assessment.subject[0])
  return !isBehaviorDataCollection && !isCoreProgressMonitoring
}

function gradesOrLevelsPhrase(assessment: AssessmentHit, isFromWizard: boolean): string {
  const grades = realGrades(assessment)
  if (showsGrades(assessment, isFromWizard) && assessment.grades) {
    const gradesInOrder = [...grades].sort((gradeA, gradeB) => gradeRank(gradeA) - gradeRank(gradeB))
    return `Grade: ${gradesInOrder.join(', ')} · `
  }
  if (showsLevels(assessment) && assessment.levels) {
    return `Level: ${assessment.levels.join(', ')} · `
  }
  return ''
}

function categoriesPhrase(assessment: AssessmentHit): string {
  if (!assessment.domains) {
    return ''
  }
  return ` (${assessment.domains.join(', ')})`
}

// The subjects shown are the ones the user's profile offers, so a no-SEL profile sees
// only Behavior & SEL's replacement name.
export function assessmentDetails(assessment: AssessmentHit, profileSubjects: string[], isFromWizard: boolean): string {
  const shownSubjects = subjectsOf(assessment).filter((subject) => profileSubjects.includes(subject))
  return `${gradesOrLevelsPhrase(assessment, isFromWizard)}${shownSubjects.join(', ')}${categoriesPhrase(assessment)}`
}
